import {
  APP_INITIALIZER,
  FactoryProvider,
  Inject,
  Injectable,
  InjectionToken,
  OnDestroy,
  Optional
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, createUrlTreeFromSnapshot } from '@angular/router';

import { BehaviorSubject, Observable, Subscription, distinctUntilChanged, filter, map } from 'rxjs';

import { BreadcrumbItem } from '@hdms/shared/common-types';

import { urlAfterRedirectsEqual } from '../helpers/route-utils';

export interface RouterBreadcrumbTranslate {
  i18nNamespaceProp: string;
  rootBreadCrumbItem: BreadcrumbItem;
  translate(breadcrumbItems: BreadcrumbItem[], ns?: string[]): BreadcrumbItem[];
}

export const ROUTER_BREADCRUMB_TRANSLATE = new InjectionToken<RouterBreadcrumbTranslate>(
  'shrd-utc.routerBreadcrumbTranslate'
);

class DefaultBreadcrumbTranslate implements RouterBreadcrumbTranslate {
  i18nNamespaceProp: string;
  rootBreadCrumbItem: BreadcrumbItem;
  translate(breadcrumbItems: BreadcrumbItem[]): BreadcrumbItem[] {
    return breadcrumbItems;
  }
}

function getUrlSlice(segments: string[], index: number): string {
  return segments.slice(0, index).join('/');
}

function createBreadCrumbItem(
  breadcrumb: BreadcrumbItem,
  route: ActivatedRoute,
  urlSegments: string[],
  index: number
): BreadcrumbItem {
  const breadcrumbItem = { ...breadcrumb };
  if ((route.component && breadcrumb.routerLink !== false) || breadcrumb.routerLink === true) {
    // in case of Angular routes, always compose the path based on router URL segments
    breadcrumbItem.routerLink = true;
    breadcrumbItem.href = getUrlSlice(urlSegments, index);
    const snapshot = route.snapshot;
    if (snapshot.routeConfig.path.includes(':')) {
      breadcrumbItem.routeParams = snapshot.params;
    }
  } else if (breadcrumb.href && !breadcrumb.href.startsWith('/')) {
    // in case of relative regular URLs, compose the path based on router URL
    // but use breadcrumb.href as last segment
    breadcrumbItem.href = `${getUrlSlice(urlSegments, index - 1)}/${breadcrumb.href}`;
  }

  return breadcrumbItem;
}

@Injectable({
  providedIn: 'root'
})
export class RouterBreadcrumbService implements OnDestroy {
  private readonly breadcrumbItems = new BehaviorSubject<BreadcrumbItem[]>([]);
  private readonly routeSubscription: Subscription;

  constructor(
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    @Optional()
    @Inject(ROUTER_BREADCRUMB_TRANSLATE)
    private readonly translateService: RouterBreadcrumbTranslate
  ) {
    if (!translateService) {
      this.translateService = new DefaultBreadcrumbTranslate();
    }
    this.routeSubscription = this.subscribeToRouterEvents();
  }

  // will be executed in SSR env
  ngOnDestroy(): void {
    this.routeSubscription.unsubscribe();
  }

  getBreadcrumbItems$(): Observable<BreadcrumbItem[]> {
    return this.breadcrumbItems.asObservable();
  }

  private subscribeToRouterEvents(): Subscription {
    return this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        distinctUntilChanged<NavigationEnd>(urlAfterRedirectsEqual),
        // remove hash/query fragment, and reuses CustomUrlSerializer to strip language prefix, from current url
        map(_ => createUrlTreeFromSnapshot(this.activatedRoute.snapshot, []).toString())
      )
      .subscribe(url => {
        this.breadcrumbItems.next(this.createBreadcrumbItems(url));
      });
  }

  private createBreadcrumbItems(url: string): BreadcrumbItem[] {
    const urlSegments = url.split('/');
    const breadcrumbItems = [this.translateService.rootBreadCrumbItem];
    const i18nNamespaceProp = this.translateService.i18nNamespaceProp;
    const i18nNamespaces = [];

    let route = this.activatedRoute;
    let index = 1;

    while (route.firstChild) {
      index++;
      route = route.firstChild;
      const firstChild = route.firstChild;
      const snapshot = route.snapshot;
      const breadcrumb = { ...snapshot.routeConfig.data?.breadcrumb };

      // skip the current route, if
      // it's either the last route in route config
      // or it's the second last route and last route has an empty path configured
      const skipRoute = !firstChild || (!firstChild.firstChild && !firstChild.snapshot.url.length);

      if (!skipRoute) {
        if (Object.keys(breadcrumb).length) {
          i18nNamespaces.unshift(snapshot.data?.[i18nNamespaceProp]);
          breadcrumbItems.push(createBreadCrumbItem(breadcrumb, route, urlSegments, index));
        } else if (route.routeConfig.path === '') {
          // it's just a container route without path and breadcrumb but children.
          // it doesn't appear in url and is just for structuring purpose,
          // so decrement the index
          index--;
        }
      }
    }

    return this.translateService.translate(breadcrumbItems, i18nNamespaces.filter(Boolean));
  }
}

export const ROUTER_BREADCRUMB_SERVICE_PROVIDER: FactoryProvider = {
  provide: APP_INITIALIZER,
  deps: [RouterBreadcrumbService],
  useFactory: () => () => null,
  multi: true
};
